import React, { useContext, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { TradingContext } from '../contexts/TradingContext';
import { BannerContext } from '../contexts/BannerContext';
import { EnforcementAlertsContext } from '../contexts/EnforcementAlertsContext';
import { useCreateOrder, useCancelOrder } from '../hooks/useTradeStation';
import { generateClosePositionPayload } from '../services/tradestationApi';
import { cancelOrders, getOpenOrders } from '../services/tradestationUtils';
import { isAfterRegularTradingHours } from '../utils/marketHours';
import { useTheme } from '../contexts/ThemeContext';
import { getRuleTypeColor } from '../utils/tradingRulesRuleTypeColors';
import LockoutActiveBanner, { getLockoutBannerAccent } from './TradingRules/LockoutActiveBanner';

/**
 * Consolidated modal for ALL enforcement alerts (max_loss_per_day, max_loss_per_position, max_trades_per_day, etc.)
 *
 * Alert field mapping from trading_lockouts:
 *   alert.account_id          → account identifier
 *   alert.rule_type           → 'max_loss_per_day' | 'max_loss_per_position' | 'max_trades_per_day' | etc.
 *   alert.threshold_value     → numeric limit
 *   alert.actual_value        → numeric actual
 *   alert.expires_at          → expiry timestamp
 *   alert.starts_at           → when triggered
 *   alert.symbol              → position symbol (for position-specific alerts)
 *   alert.context             → JSONB with { positionSnapshot: { Symbol, Quantity, AveragePrice } }
 */
const EnforcementAlertsModal = ({ alerts }) => {
  const { colors } = useTheme();
  const { positions, isPaperTrading, isFutures, selectedTickerDetails, orders } = useContext(TradingContext);
  const { showMessage } = useContext(BannerContext);
  const { acknowledgeAlert } = useContext(EnforcementAlertsContext);
  const { createOrder } = useCreateOrder();
  const { cancelOrder } = useCancelOrder();

  const [closingPositions, setClosingPositions] = useState({});
  const [acknowledging, setAcknowledging] = useState({});
  const [isClosed, setIsClosed] = useState(false);
  const [showConfirmCloseAll, setShowConfirmCloseAll] = useState(false);
  const [pendingCloseAllAlert, setPendingCloseAllAlert] = useState(null);
  const [closeAllConfirmStep, setCloseAllConfirmStep] = useState(1);

  // Reset closed state when alerts change (new alerts arrive)
  React.useEffect(() => {
    if (alerts && alerts.length > 0) {
      setIsClosed(false);
    }
  }, [alerts?.length]);

  if (!alerts || alerts.length === 0 || isClosed) return null;

  const formatCurrency = (value) => {
    if (value == null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const isCountRule = (ruleType) =>
    ruleType === 'max_trades_per_day' || ruleType === 'consecutive_loss_cooldown';
  const isCurrencyRule = (ruleType) =>
    ruleType === 'max_loss_per_day' ||
    ruleType === 'max_loss_per_position' ||
    ruleType === 'profit_target_per_day';
  const isLossCurrencyRule = (ruleType) =>
    ruleType === 'max_loss_per_day' || ruleType === 'max_loss_per_position';

  const formatLimit = (alert) => {
    if (alert.threshold_value == null) return '—';
    if (isCountRule(alert.rule_type)) return String(Math.round(alert.threshold_value));
    if (isCurrencyRule(alert.rule_type)) return formatCurrency(alert.threshold_value);
    return String(alert.threshold_value);
  };

  const formatActual = (alert) => {
    if (alert.actual_value == null) return '—';
    if (isCountRule(alert.rule_type)) return String(Math.round(alert.actual_value));
    if (isLossCurrencyRule(alert.rule_type)) return formatCurrency(-Math.abs(alert.actual_value));
    if (alert.rule_type === 'profit_target_per_day') return formatCurrency(Math.abs(alert.actual_value));
    return String(alert.actual_value);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit'
      });
    } catch (_) { return 'N/A'; }
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) return 'N/A';
    try {
      const expiry = new Date(expiresAt);
      if (expiry < new Date()) return 'Expired';
      return expiry.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    } catch (_) { return 'N/A'; }
  };

  const getRuleTypeLabel = (ruleType) => {
    switch (ruleType) {
      case 'max_loss_per_day':     return 'Max Daily Loss';
      case 'profit_target_per_day': return 'Target Daily Profit';
      case 'consecutive_loss_cooldown': return 'Consecutive Loss Cooldown';
      case 'max_loss_per_position': return 'Position Loss';
      case 'max_trades_per_day':   return 'Max Daily Trades';
      case 'trading_window':        return 'Trading Hours';
      case 'manual_lockout':        return 'Manual Lockout';
      default: return ruleType || 'Alert';
    }
  };

  const getLockoutStatus = (alert) => {
    if (alert.expires_at && new Date(alert.expires_at) <= new Date()) return 'inactive';
    return 'active';
  };

  const isDailyLockout = (alert) =>
    alert.rule_type === 'max_loss_per_day';

  const isPositionAlert = (alert) =>
    alert.rule_type === 'max_loss_per_position';

  const hasDailyAlert = alerts.some(isDailyLockout);
  const latestActiveLockout = alerts
    .filter((a) => getLockoutStatus(a) === 'active')
    .reduce((latest, current) => {
      if (!latest) return current;
      const latestTs = latest.expires_at ? new Date(latest.expires_at).getTime() : -Infinity;
      const currentTs = current.expires_at ? new Date(current.expires_at).getTime() : -Infinity;
      return currentTs > latestTs ? current : latest;
    }, null);
  const latestActiveExpiryText = latestActiveLockout?.expires_at
    ? (() => {
        const dt = new Date(latestActiveLockout.expires_at);
        if (Number.isNaN(dt.getTime())) return null;
        const time = dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true }).replace(' ', '');
        const date = dt.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: '2-digit' });
        return `${time} ${date}`;
      })()
    : null;

  const handleAcknowledge = async (alertId, userAction) => {
    setAcknowledging(prev => ({ ...prev, [alertId]: true }));
    try {
      // acknowledgeAlert applies the server-returned lockout row (with dismissed_at).
      // No second pending fetch here — that raced and could overwrite with a stale response.
      await acknowledgeAlert(alertId, userAction);
    } catch (err) {
      console.error('[EnforcementAlertsModal] Error acknowledging alert:', err);
    } finally {
      setAcknowledging(prev => ({ ...prev, [alertId]: false }));
    }
  };

  const handleClosePosition = async (alert) => {
    const alertId = alert.id;
    const accountId = alert.account_id;
    const isDaily = isDailyLockout(alert);

    // For "Close ALL Positions" (daily alerts), show two-step confirmation first
    if (isDaily) {
      setPendingCloseAllAlert(alert);
      setCloseAllConfirmStep(1);
      setShowConfirmCloseAll(true);
      return;
    }

    // For single position closes, proceed directly
    await executeClosePosition(alert);
  };

  const executeClosePosition = async (alert) => {
    const alertId = alert.id;
    const accountId = alert.account_id;
    const isDaily = isDailyLockout(alert);

    if (closingPositions[alertId]) return;
    setClosingPositions(prev => ({ ...prev, [alertId]: true }));

    try {
      if (isDaily) {
        let positionsToClose = Array.isArray(positions)
          ? positions.filter(p => p.AccountID === accountId && Math.abs(parseFloat(p?.Quantity) || 0) > 0)
          : [];

        if (positionsToClose.length === 0) {
          showMessage('No open positions to close', 'info', true, 'notification');
          await handleAcknowledge(alertId, 'closed_all_positions');
          return;
        }

        // Get orders to cancel first (for confirmation display)
        const allOpenOrders = await getOpenOrders(orders, null, { AccountID: accountId });

        showMessage('Canceling related orders...', 'info', true, 'notification');
        if (allOpenOrders.length > 0) {
          await cancelOrders(isPaperTrading, allOpenOrders, cancelOrder, showMessage);
        }

        const positionWord = positionsToClose.length === 1 ? 'position' : 'positions';
        showMessage(`Closing ${positionsToClose.length} ${positionWord}...`, 'info', true, 'notification');

        const promises = positionsToClose.map(async (pos) => {
          try {
            // Validate position has required fields
            if (!pos.LongShort || (pos.LongShort !== 'Long' && pos.LongShort !== 'Short')) {
              throw new Error(`Invalid position data: LongShort is "${pos.LongShort || 'missing'}"`);
            }
            if (!pos.Symbol) {
              throw new Error('Invalid position data: Symbol is missing');
            }
            if (!pos.Quantity || Math.abs(parseFloat(pos.Quantity)) === 0) {
              throw new Error('Invalid position data: Quantity is zero or missing');
            }

            const payload = generateClosePositionPayload({ AccountID: accountId }, pos, isFutures, selectedTickerDetails);
            const res = await createOrder(!!isPaperTrading, payload);
            if (res?.Error) throw new Error(res?.Message || 'Unknown error');
            if (res?.Orders) {
              const failed = res.Orders.filter(o => o.Error === 'FAILED');
              if (failed.length > 0) throw new Error(failed.map(o => o.Message || 'Unknown').join('; '));
            }
            return { symbol: pos.Symbol, ok: true };
          } catch (err) {
            return { symbol: pos?.Symbol || 'Unknown', ok: false, error: err?.message || String(err) };
          }
        });

        const results = await Promise.allSettled(promises);
        const flattened = results.map(r => r.status === 'fulfilled' ? r.value : r.reason);
        const successes = flattened.filter(r => r?.ok);
        const failures = flattened.filter(r => !r?.ok);

        await handleAcknowledge(alertId, 'closed_all_positions');

        if (failures.length === 0) {
          const tickers = successes.map(s => s.symbol).join(', ');
          let message = `Close orders submitted for ${successes.length} position(s): ${tickers}`;
          if (isAfterRegularTradingHours()) message += '. Note: Using limit orders during extended hours.';
          showMessage(message, 'success', true, 'notification');
        } else {
          const failedList = failures.map(f => `${f.symbol}: ${f.error}`).join('; ');
          showMessage(`Some positions failed to close: ${failedList}`, 'warning', false, 'notification');
        }
      } else {
        // Position-specific alert
        const symbol = alert.symbol
          || alert.context?.positionSnapshot?.Symbol
          || 'Unknown';

        const position = positions?.find(p => p.AccountID === accountId && p.Symbol === symbol);

        if (!position) {
          showMessage(`Position ${symbol} not found - it may have already been closed`, 'warning', true, 'notification');
          await handleAcknowledge(alertId, 'closed_position');
          return;
        }

        const allOpenOrders = await getOpenOrders(orders, null, { AccountID: accountId });
        const symbolOrders = allOpenOrders.filter(o => o.Legs?.[0]?.Symbol === symbol);
        if (symbolOrders.length > 0) {
          showMessage(`Canceling open orders for ${symbol}...`, 'info', true, 'notification');
          await cancelOrders(isPaperTrading, symbolOrders, cancelOrder, showMessage);
        }

        // Validate position has required fields
        if (!position.LongShort || (position.LongShort !== 'Long' && position.LongShort !== 'Short')) {
          throw new Error(`Invalid position data: LongShort is "${position.LongShort || 'missing'}"`);
        }

        showMessage(`Closing ${symbol} position...`, 'info', true, 'notification');
        const payload = generateClosePositionPayload({ AccountID: accountId }, position, isFutures, selectedTickerDetails);
        const res = await createOrder(!!isPaperTrading, payload);

        if (res?.Error) throw new Error(res?.Message || 'Unknown error');
        if (res?.Orders) {
          const failed = res.Orders.filter(o => o && (o.Error === 'FAILED' || o.Status === 'FAILED'));
          if (failed.length > 0) throw new Error(failed.map(o => o.Message || o.Reason || 'Unknown').join('; '));
        }

        let message = `Close order submitted for ${symbol}`;
        if (isAfterRegularTradingHours()) message += '. Note: Using limit order during extended hours.';
        showMessage(message, 'success', true, 'notification');

        await handleAcknowledge(alertId, 'closed_position');
      }
    } catch (err) {
      console.error('[EnforcementAlertsModal] Error closing position:', err);
      showMessage(`Failed to close position: ${err.message}`, 'danger', false, 'notification');
    } finally {
      setClosingPositions(prev => ({ ...prev, [alertId]: false }));
    }
  };

  const handleClose = () => {
    // Just close the modal without acknowledging alerts
    // On page refresh, un-acknowledged alerts will show again
    setIsClosed(true);
  };

  const handleConfirmCloseAll = async () => {
    if (closeAllConfirmStep === 1) {
      setCloseAllConfirmStep(2);
      return;
    }
    setShowConfirmCloseAll(false);
    setCloseAllConfirmStep(1);
    if (pendingCloseAllAlert) {
      await executeClosePosition(pendingCloseAllAlert);
      setPendingCloseAllAlert(null);
    }
  };

  const handleCancelCloseAll = () => {
    setShowConfirmCloseAll(false);
    setCloseAllConfirmStep(1);
    setPendingCloseAllAlert(null);
  };

  // Get positions and orders for confirmation modal
  const getCloseAllConfirmationData = () => {
    if (!pendingCloseAllAlert) return { positions: [], orders: [] };
    const accountId = pendingCloseAllAlert.account_id;
    const positionsToClose = Array.isArray(positions)
      ? positions.filter(p => p.AccountID === accountId && Math.abs(parseFloat(p?.Quantity) || 0) > 0)
      : [];
    const allOpenOrders = Array.isArray(orders)
      ? orders.filter(o => o.AccountID === accountId && ['ACK', 'DON', 'OPN'].includes(o.Status))
      : [];
    return { positions: positionsToClose, orders: allOpenOrders };
  };

  const confirmationData = getCloseAllConfirmationData();

  return (
    <Modal show={true} onHide={handleClose} backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header style={{
        backgroundColor: colors.bgSecondary,
        color: colors.text,
        borderBottom: `1px solid ${colors.borderLight}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Modal.Title style={{ color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
          Rule Violations
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px',
            height: '24px',
            padding: '0 8px',
            borderRadius: '12px',
            backgroundColor: hasDailyAlert ? colors.danger : colors.warning,
            color: '#ffffff',
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1
          }}>
            {alerts.length}
          </span>
        </Modal.Title>
        <button
          onClick={handleClose}
          style={{
            background: 'none',
            border: 'none',
            color: colors.text,
            fontSize: '1.5rem',
            fontWeight: 300,
            cursor: 'pointer',
            padding: 0,
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = colors.bgTertiary}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          aria-label="Close"
        >
          ×
        </button>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: colors.bgPrimary, color: colors.text, padding: 0 }}>
        {latestActiveLockout && (
          <LockoutActiveBanner
            endsAtText={latestActiveExpiryText}
            style={{ margin: '12px 12px 0 12px' }}
          />
        )}
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>Timestamp</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>Account</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>Type</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 500 }}>Symbol</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: colors.textMuted, fontWeight: 500 }}>Limit</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: colors.textMuted, fontWeight: 500 }}>Actual</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: colors.textMuted, fontWeight: 500 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert, index) => {
                const isDaily = isDailyLockout(alert);
                const isPosition = isPositionAlert(alert);
                const isClosing = closingPositions[alert.id];
                const isAcking = acknowledging[alert.id];
                const lockoutStatus = getLockoutStatus(alert);
                const canClose = isDaily || isPosition;
                const typeAccent = getRuleTypeColor(alert.rule_type, colors);
                const activeAccent = getLockoutBannerAccent(colors);

                // Resolve symbol for display
                const symbol = alert.symbol
                  || alert.context?.positionSnapshot?.Symbol
                  || null;

                return (
                  <tr key={alert.id} style={{ borderBottom: index < alerts.length - 1 ? `1px solid ${colors.border}` : 'none' }}>
                    <td style={{ padding: '10px 12px', color: colors.text, whiteSpace: 'nowrap' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 9999,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        backgroundColor:
                          lockoutStatus === 'inactive'
                              ? `${colors.textMuted}18`
                              : `${activeAccent}18`,
                        color:
                          lockoutStatus === 'inactive'
                              ? colors.textMuted
                              : activeAccent,
                        border:
                          lockoutStatus === 'inactive'
                              ? `1px solid ${colors.border}`
                              : `1px solid ${activeAccent}`,
                        whiteSpace: 'nowrap'
                      }}>
                        {lockoutStatus === 'inactive' ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '0.8rem', color: colors.text, whiteSpace: 'nowrap' }}>
                      {formatTimestamp(alert.starts_at)}
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.text, whiteSpace: 'nowrap' }}>
                      {alert.account_id}
                    </td>
                    <td style={{ padding: '10px 12px', color: colors.text, whiteSpace: 'nowrap' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        backgroundColor: `${typeAccent}20`,
                        color: typeAccent,
                        whiteSpace: 'nowrap'
                      }}>
                        {getRuleTypeLabel(alert.rule_type)}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: colors.text }}>
                      {symbol || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.text }}>
                      {formatLimit(alert)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.chartDown, fontWeight: 600 }}>
                      {formatActual(alert)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleAcknowledge(alert.id, 'dismissed')}
                        disabled={isClosing || isAcking}
                        style={{
                          marginRight: 4,
                          fontSize: '0.75rem',
                          padding: '3px 8px',
                          backgroundColor: colors.bgTertiary,
                          borderColor: colors.borderLight
                        }}
                      >
                        {isAcking ? <Spinner animation="border" size="sm" /> : 'Dismiss'}
                      </Button>
                      {canClose && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleClosePosition(alert)}
                          disabled={isClosing || isAcking}
                          style={{
                            fontSize: '0.75rem',
                            padding: '3px 8px',
                            backgroundColor: colors.danger,
                            borderColor: colors.danger
                          }}
                        >
                          {isClosing
                            ? <Spinner animation="border" size="sm" />
                            : isDaily ? 'Close ALL Positions' : 'Close Position'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {hasDailyAlert && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: `${colors.danger}15`,
            borderTop: `1px solid ${colors.border}`,
            fontSize: '0.85rem',
            color: colors.textLight
          }}>
            🔒 <strong>Trading Locked:</strong> Daily loss or trade limit reached. You may still close existing positions until the lockout expires.
          </div>
        )}
      </Modal.Body>

      {/* Close ALL Positions Confirmation Modal (two-step) */}
      <Modal show={showConfirmCloseAll} onHide={handleCancelCloseAll} centered size="md">
        <Modal.Header style={{
          backgroundColor: colors.bgSecondary,
          color: colors.text,
          borderBottom: `1px solid ${colors.borderLight}`
        }}>
          <Modal.Title style={{ color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
            {closeAllConfirmStep === 1
              ? '⚠️ Confirm Close ALL Positions'
              : '⚠️ Final Confirmation'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: colors.bgPrimary, color: colors.text }}>
          {closeAllConfirmStep === 1 ? (
            <>
              <p style={{ marginBottom: '1rem' }}>
                This will <strong>cancel all open orders</strong> and <strong>close all positions</strong> for account <code>{pendingCloseAllAlert?.account_id}</code>.
              </p>

              {confirmationData.orders.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ color: colors.text }}>Orders to cancel ({confirmationData.orders.length}):</strong>
                  <div style={{
                    backgroundColor: colors.bgSecondary,
                    padding: '0.75rem',
                    borderRadius: '4px',
                    marginTop: '0.5rem',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    fontSize: '0.85rem'
                  }}>
                    {confirmationData.orders.slice(0, 10).map((order, idx) => {
                      const leg = order.Legs?.[0] || {};
                      const symbol = leg.Symbol || order.Symbol || 'Unknown';
                      const qty = leg.QuantityOrdered || order.Quantity || '';
                      const side = leg.BuyOrSell || '';
                      return (
                        <div key={idx} style={{ marginBottom: '0.25rem' }}>
                          {symbol} {side} {qty} ({order.OrderType || 'Market'})
                        </div>
                      );
                    })}
                    {confirmationData.orders.length > 10 && (
                      <div style={{ color: colors.textMuted, fontStyle: 'italic' }}>
                        ... and {confirmationData.orders.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {confirmationData.positions.length > 0 && (
                <div>
                  <strong style={{ color: colors.text }}>Positions to close ({confirmationData.positions.length}):</strong>
                  <div style={{
                    backgroundColor: colors.bgSecondary,
                    padding: '0.75rem',
                    borderRadius: '4px',
                    marginTop: '0.5rem',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    fontSize: '0.85rem'
                  }}>
                    {confirmationData.positions.slice(0, 10).map((pos, idx) => (
                      <div key={idx} style={{ marginBottom: '0.25rem' }}>
                        {pos.Symbol} {pos.LongShort} {Math.abs(parseFloat(pos.Quantity || 0))}
                      </div>
                    ))}
                    {confirmationData.positions.length > 10 && (
                      <div style={{ color: colors.textMuted, fontStyle: 'italic' }}>
                        ... and {confirmationData.positions.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(confirmationData.orders.length === 0 && confirmationData.positions.length === 0) && (
                <p style={{ color: colors.textMuted, fontStyle: 'italic' }}>
                  No open orders or positions found.
                </p>
              )}
            </>
          ) : (
            <p style={{ marginBottom: 0 }}>
              Are you sure you want to <strong>close ALL positions</strong> and <strong>cancel all open orders</strong> for account{' '}
              <code>{pendingCloseAllAlert?.account_id}</code>? This cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.borderLight}` }}>
          <Button variant="secondary" onClick={handleCancelCloseAll} size="sm">
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmCloseAll} size="sm">
            {closeAllConfirmStep === 1 ? 'Continue' : 'Yes, Close ALL Positions'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default EnforcementAlertsModal;
